import { assertServiceHost, axiosClient } from '@nx-frontend/shared/api';
import {
  OrgServiceOrgDto,
  ServicePayload,
  WorkspaceAvatarDto,
  WorkspaceDto,
  WorkspaceHtmlDto,
  WorkspaceUsageDto,
} from '@nx-frontend/shared/types';

export const getAllWorkspaces = async ({
  host,
  userId,
  includeOrgType = true,
  includeAvatar = false,
}: ServicePayload & { includeOrgType?: boolean; includeAvatar?: boolean }): Promise<WorkspaceDto[]> => {
  const params: Record<string, unknown> = {};
  if (includeOrgType) {
    params['returnOrgType'] = true;
  }
  if (includeAvatar) {
    params['returnAvatar'] = true;
  }
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces`, {
    userId,
    params,
  });
  return response.data;
};

interface GetWorkspacePayload extends ServicePayload {
  workspaceId: string;
}

interface UpdateWorkspacePayload extends ServicePayload {
  workspaceId: string;
  title?: string;
}

export const getWorkspace = async ({ host, userId, workspaceId }: GetWorkspacePayload): Promise<WorkspaceDto> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces/${workspaceId}`, { userId });
  return response.data;
};

export const countWorkspaceMembers = async ({
  host,
  userId,
  workspaceId,
}: GetWorkspacePayload): Promise<{ count: number }> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces/${workspaceId}/countMembers`, {
    userId,
  });
  return response.data;
};

export const getWorkspaceAvatar = async ({
  host,
  userId,
  workspaceId,
}: GetWorkspacePayload): Promise<WorkspaceAvatarDto> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces/${workspaceId}/avatar`, { userId });
  return response.data;
};

/**
 * The same as getWorkspaceAvatar, but for multiple workspaces
 */
export const getWorkspacesAvatars = async ({
  host,
  userId,
  workspaceIds,
}: ServicePayload & { workspaceIds: string[] }): Promise<WorkspaceAvatarDto[]> => {
  const result: WorkspaceAvatarDto[] = [];

  // split workspaceIds into chunks of 50 to avoid hitting the URL length limit
  const chunkSize = 50;
  const chunks: string[][] = [];
  for (let i = 0; i < workspaceIds.length; i += chunkSize) {
    chunks.push(workspaceIds.slice(i, i + chunkSize));
  }

  for (const workspacesIdsChunk of chunks) {
    const response = await axiosClient.get<WorkspaceAvatarDto[]>(`${assertServiceHost(host)}/v1/avatars`, {
      userId,
      filter: {
        workspaceId: workspacesIdsChunk,
      },
    });
    result.push(...response.data);
  }

  return result;
};

export const getWorkspaceUsage = async ({
  host,
  userId,
  workspaceId,
}: GetWorkspacePayload): Promise<WorkspaceUsageDto> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces/${workspaceId}/usage`, { userId });
  return response.data;
};

export const getWorkspaceHtmls = async ({
  host,
  userId,
  workspaceId,
}: GetWorkspacePayload): Promise<WorkspaceHtmlDto[]> => {
  const response = await axiosClient.get(`${assertServiceHost(host)}/v1/workspaces/${workspaceId}/htmls`, { userId });
  return response.data;
};

export const getWorkspaceMentions = async ({ host, userId, workspaceId }: GetWorkspacePayload): Promise<void> => {
  const response = await axiosClient.get(
    `${assertServiceHost(host)}/v1/workspaces/${workspaceId}/mentions/workspace/${workspaceId}`,
    { userId }
  );
  return response.data;
};

export const updateWorkspace = async ({ host, userId, workspaceId, ...restProps }: UpdateWorkspacePayload) => {
  const response = await axiosClient.post<OrgServiceOrgDto[]>(
    `${assertServiceHost(host)}/v1/workspaces/${workspaceId}`,
    {
      ...restProps,
    },
    {
      userId,
    }
  );
  return response.data;
};
